/**
 * Order pipeline, redesigned 2026-09-16 on the template's own pieces:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/job-board/dashboard/top-referrers.tsx
 *   the label · figure · bar rows, one per lifecycle stage (as `OrdersByStatusTile` already does);
 * vendor/isomorphic/apps/isomorphic/src/app/shared/appointment/appointment-list/stats.tsx
 *   the "needs a look" figure cards across the top;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/profile/profile-header.tsx
 *   the hero, through `components/detail-parts.tsx`.
 *
 * 008-reporting-dashboards T024 (US4). `GET /api/orders/admin/orders/pipeline` answers a live
 * snapshot — `{state: count}`, no query parameters, no date range — the same query the dashboard's
 * `OrdersByStatusTile` reads (`getOrderPipeline()`, reused rather than copied). FR-018/FR-021 (range
 * in the URL, no unbounded range) presuppose a date dimension this endpoint does not have; that
 * departure is deliberate, not an oversight.
 *
 * Every `ORDER_STATES` entry renders, zero included (constitution VI), and any extra key the API
 * returns (live: `cart`) is shown under "Closed and other states" rather than dropped. The phases
 * are layout only: no phase shows a total, because the API answers per state and a phase sum would
 * be a figure it never computed (constitution I). A bar's length is the stage's own count drawn
 * against the largest stage — pixels for the eye; the number printed beside it is the API's.
 *
 * Gated on `orders.read` by `nav.tsx`'s `/reports/pipeline` entry through the root loader, so there
 * is no component-level `can()` here — same as `Staleness.tsx`.
 */
import { Link, useLoaderData, useRevalidator } from "react-router-dom";
import {
  PiArrowClockwiseBold as RefreshIcon,
  PiArrowRightBold as ArrowRight,
  PiCaretRightBold as CaretRight,
  PiChartBarDuotone as ChartIcon,
  PiClipboardTextDuotone as IntakeIcon,
  PiPackageDuotone as WarehouseIcon,
  PiProhibitDuotone as ClosedIcon,
  PiReceiptDuotone as ReceiptIcon,
  PiTruckDuotone as TruckIcon,
  PiUserCheckDuotone as ApproveIcon,
  PiWalletDuotone as WalletIcon,
} from "react-icons/pi";
import type { ReactNode } from "react";
import { Badge, Button, Text } from "rizzui";
import { MetricCard, WidgetCard, getStatusBadge } from "@steelyard/theme";
import { CardTitle, PageHero } from "../../components/detail-parts";
import { formatCount } from "../../components/dashboard/TrendStatCard";
import { INERT, STEEL } from "../../components/dashboard/palette";
import { ORDER_STATES, getOrderPipeline, money, stateLabel } from "../../lib/services/ordering";

export async function pipelineReportLoader() {
  return { pipeline: await getOrderPipeline() };
}

type Phase = { title: string; hint: string; icon: ReactNode; tile: string; states: string[] };

/** The lifecycle, left to right. Each names the `ORDER_STATES` it holds, in their own order. */
const PHASES: Phase[] = [
  {
    title: "Intake",
    hint: "Placed, waiting on payment or an approval.",
    icon: <IntakeIcon aria-hidden className="size-5" />,
    tile: "bg-secondary-lighter text-secondary-dark",
    states: ["placed", "awaiting_payment", "awaiting_company_approval", "awaiting_approval"],
  },
  {
    title: "Warehouse",
    hint: "Approved, being picked and packed.",
    icon: <WarehouseIcon aria-hidden className="size-5" />,
    tile: "bg-blue-lighter text-blue-dark",
    states: ["approved", "picking_packing", "packed"],
  },
  {
    title: "Fulfilment",
    hint: "Invoiced, on the road, delivered.",
    icon: <TruckIcon aria-hidden className="size-5" />,
    tile: "bg-green-lighter text-green-dark",
    states: ["invoiced", "dispatched", "delivered"],
  },
];

/** The stages someone usually has to act on or chase. Each card is one state's own count — never a sum. */
const ATTENTION: { state: string; title: string; icon: ReactNode; tile: string }[] = [
  { state: "awaiting_approval", title: "Awaiting approval", icon: <ApproveIcon aria-hidden className="size-6" />, tile: "bg-orange-lighter text-orange-dark" },
  { state: "awaiting_payment", title: "Awaiting payment", icon: <WalletIcon aria-hidden className="size-6" />, tile: "bg-blue-lighter text-blue-dark" },
  { state: "packed", title: "Packed, not yet invoiced", icon: <ReceiptIcon aria-hidden className="size-6" />, tile: "bg-secondary-lighter text-secondary-dark" },
  { state: "dispatched", title: "Dispatched, not yet delivered", icon: <TruckIcon aria-hidden className="size-6" />, tile: "bg-green-lighter text-green-dark" },
];

const ordersFor = (state: string) => `/orders?state=${encodeURIComponent(state)}`;

export default function PipelineReportPage() {
  const { pipeline } = useLoaderData() as Awaited<ReturnType<typeof pipelineReportLoader>>;
  const revalidator = useRevalidator();

  const countOf = (state: string) => money(pipeline[state]);
  const phased = new Set(PHASES.flatMap((phase) => phase.states));
  // Named states in lifecycle order, then any key the API returns that `ORDER_STATES` does not name.
  const others = [...new Set([...ORDER_STATES.map((row) => row.state), ...Object.keys(pipeline)])].filter((state) => !phased.has(state));
  const largest = Math.max(1, ...Object.keys(pipeline).map((state) => countOf(state) ?? 0));

  return (
    <div className="@container flex flex-col gap-6">
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Reports", href: "/reports" }, { name: "Order Pipeline" }]}
        back={{ href: "/orders", label: "All orders" }}
        icon={<ChartIcon aria-hidden className="size-9" />}
        iconClassName="bg-blue"
        title="Order Pipeline"
        description="Every order by where it stands right now. Select any stage to open those orders."
        meta={
          <>
            <Badge variant="flat" color="success" rounded="pill" className="gap-1.5">
              <span aria-hidden className="size-1.5 animate-pulse rounded-full bg-green" />
              Live snapshot
            </Badge>
            <Badge variant="outline" rounded="pill" className="border-muted text-gray-600">
              Not a period report — no date range applies
            </Badge>
          </>
        }
        actions={
          <Button type="button" variant="outline" isLoading={revalidator.state === "loading"} onClick={() => revalidator.revalidate()}>
            <RefreshIcon aria-hidden className="me-1.5 size-4" />
            Refresh counts
          </Button>
        }
      />

      <section aria-labelledby="pipeline-attention" className="flex flex-col gap-3">
        <Text id="pipeline-attention" className="text-xs font-semibold uppercase tracking-wider text-gray-500">
          Needs a look
        </Text>
        <div className="grid gap-4 @lg:grid-cols-2 @6xl:grid-cols-4">
          {ATTENTION.map((card) => {
            const count = countOf(card.state);
            return (
              <Link key={card.state} to={ordersFor(card.state)} className="group rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
                <MetricCard
                  title={card.title}
                  metric={count === null ? "Not reported" : formatCount(count)}
                  icon={card.icon}
                  iconClassName={card.tile}
                  info={
                    <Text className="mt-1 flex items-center gap-1 text-xs font-medium text-gray-500 group-hover:text-gray-900">
                      Open these orders
                      <ArrowRight aria-hidden className="size-3 transition-transform group-hover:translate-x-0.5" />
                    </Text>
                  }
                  className="h-full transition-shadow group-hover:shadow-md"
                />
              </Link>
            );
          })}
        </div>
      </section>

      <div className="grid items-start gap-6 @5xl:grid-cols-3">
        {PHASES.map((phase, index) => (
          <WidgetCard
            key={phase.title}
            rounded="xl"
            title={
              <CardTitle icon={phase.icon} tile={phase.tile}>
                {phase.title}
              </CardTitle>
            }
            description={phase.hint}
            descriptionClassName="mt-2 text-sm text-gray-500"
            action={
              <Badge variant="flat" rounded="pill" className="whitespace-nowrap bg-gray-100 text-gray-600">
                Phase {index + 1} of {PHASES.length}
              </Badge>
            }
          >
            <ul className="-mx-3 mt-4 flex flex-col gap-1">
              {phase.states.map((state) => (
                <StageRow key={state} state={state} count={countOf(state)} largest={largest} />
              ))}
            </ul>
          </WidgetCard>
        ))}
      </div>

      <WidgetCard
        rounded="xl"
        title={
          <CardTitle icon={<ClosedIcon aria-hidden className="size-5" />} tile="bg-gray-100 text-gray-600">
            Closed and other states
          </CardTitle>
        }
        description="Cancelled orders, and any state the API reports that the lifecycle above does not name."
        descriptionClassName="mt-2 text-sm text-gray-500"
      >
        <ul className="-mx-3 mt-4 grid gap-1 @3xl:grid-cols-2 @6xl:grid-cols-3">
          {others.map((state) => (
            <StageRow key={state} state={state} count={countOf(state)} largest={largest} />
          ))}
        </ul>
      </WidgetCard>
    </div>
  );
}

/** One stage: its status label, the API's count, and a bar scaled to the largest stage. The whole row opens that stage's orders. */
function StageRow({ state, count, largest }: { state: string; count: number | null; largest: number }) {
  const figure = count === null ? "not reported" : formatCount(count);
  return (
    <li>
      <Link
        to={ordersFor(state)}
        aria-label={`${stateLabel(state)}: ${figure}. Open these orders.`}
        className="group grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 rounded-lg px-3 py-2.5 transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary dark:hover:bg-gray-100/40"
      >
        <div className="min-w-0 text-sm">{getStatusBadge(state)}</div>
        <span className="flex items-center gap-1.5 font-lexend text-base font-semibold tabular-nums text-gray-900">
          {count === null ? "—" : formatCount(count)}
          <CaretRight aria-hidden className="size-3 text-gray-400 transition-transform group-hover:translate-x-0.5" />
        </span>
        <span aria-hidden className="col-span-2 block h-1.5 rounded-full bg-gray-100">
          <span
            className="block h-1.5 rounded-full"
            style={{ width: `${count ? (count / largest) * 100 : 0}%`, backgroundColor: state === "cancelled" ? INERT : STEEL }}
          />
        </span>
      </Link>
    </li>
  );
}
